
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { IBatch } from 'src/app/models/batch.model';
import * as ForecastActions from '../../actions/forecast.actions';
import * as fromRoot from '../../reducers';
import { ProjectService } from '../../services/project/project.service';
import { IBatchForecastMeasurement } from '../../models/batch-forecast-measurement.model';
import {Router} from '@angular/router';
import {Iproject} from '../../models/project.model';
import {MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup} from "@angular/material/button-toggle";
import {TranslateModule} from "@ngx-translate/core";
import {KpiLabelPipe} from "../../shared/pipes/kpi-label.pipe";
import {MatFormField,MatLabel, MatOption, MatSelect} from "@angular/material/select";
import {LoaderComponent} from "../../shared/components/loader/loader.component";
import {MatTooltip} from "@angular/material/tooltip";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {MatSuffix} from "@angular/material/form-field";
import {MultiLineTooltipDirective} from "../../shared/directives/multi-line-tooltip/multi-line-tooltip.directive";
import {FormsModule} from "@angular/forms";


/**
 * Represents the Forecast calculation field. Manages user inputs and shows CPI accordingly.
 */
@Component({
  selector: 'colimo-forecast',
  styleUrls: ['forecast.component.scss'],
  imports: [
    MatFormField,
    TranslateModule,
    KpiLabelPipe,
    LoaderComponent,
    MatTooltip,
    MatSelect,
    MatIcon,
    MatIconButton,
    MatSuffix,
    MultiLineTooltipDirective,
    MatOption,
    FormsModule,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatLabel
  ],
  templateUrl: 'forecast.component.html'
})
export class ForecastComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public referenceBatches: any;
  @Input() public batch: IBatch;
  @Input() public project: Iproject;
  @Input() public selectedForecastMeasurement: IBatchForecastMeasurement;
  @Input() public showVectorTintingButton: boolean = false;

  @Output() eventForecastInit: EventEmitter<number> = new EventEmitter();

  public calculatedCPI;
  public selectedReference;
  public isLoading = false;

  public REF_BATCH: string = 'BATCH';
  public RETAIN_MEASUREMENT: string = 'RETAIN_MEASUREMENT';
  public selectedReferenceType: string = this.REF_BATCH;
  public retainMeasurements: IBatchForecastMeasurement[] = [];
  public selectedRetainMeasurement: IBatchForecastMeasurement;

  private storedForecasts: any[];

  private calculatedForecasts$: Observable<any>;
  private stop$: Subject<boolean> = new Subject();

  constructor(
    private projectService: ProjectService,
    private store: Store<fromRoot.State>,
    private router: Router
  ) {}

  public ngOnInit(): void {
    // Fetch calculated forecasts from store
    this.calculatedForecasts$ = this.store.select(fromRoot.getCalculatedForecasts);
    // stored forecasts will be queried after retainMeasurements have loaded
    this.loadRetainMeasurements();
    // #508439 Update forecast measurements should only filter by productNr - trigger reload on update forecast measurements
    this.projectService.retainSampleReloadTrigger$.pipe(takeUntil(this.stop$)).subscribe(() => {
      this.loadRetainMeasurements();
    });
  }

  public ngOnDestroy(): void {
    this.stop$.next(true);
    this.stop$.unsubscribe();
  }

  /**
   * Is called when the selected forecast measurement is changed
   * @param changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedForecastMeasurement) {
      if (!changes.selectedForecastMeasurement.currentValue) {
        this.calculatedCPI = undefined;
      } else if (changes.selectedForecastMeasurement.currentValue && this.selectedReference) {
        this.showSelectedForecast();
      }
    }
  }

  public selectedReferenceChanged(event: any): void {
    if (event.value) {
      this.showSelectedForecast();
    }
  }

  public resetSelectedReference(): void {
    this.selectedReference = undefined;
    this.calculatedCPI = undefined;
  }

  public selectedReferenceTypeChanged(event: MatButtonToggleChange): void {
    this.selectedReferenceType = event.value;
  }

  public selectedRetainMeasurementChanged(event: any) {
    if (!event || !event.value) {
      this.selectedRetainMeasurement = null;
    } else {
      this.selectedRetainMeasurement = event.value;
    }
    // update forecast display here
    this.showSelectedForecast()
  }

  /**
   * Handle calculation button click.
   */
  public onSubmit(): void {
    if (!this.selectedReference || !this.selectedForecastMeasurement) {
      return;
    }
    this.isLoading = true;

    this.loadCalculatedForecast().pipe(take(1)).subscribe((forecast: IBatch) => {
      const calculatedForecast = this.createForecastObject(forecast);
      this.addForecastToStore(calculatedForecast);
    });
  }

  public navigateToVectorTintingPage(): void {
    const routerLink = ['/color', 'vector-tinting', 'project', this.project.id, 'batch', this.batch.batchId, 'vector'];
    // create project with only the forecast product
    const projectForVector = {
      batchNumber: this.batch.batchNumber,
      batches: [this.batch],
      gradient: this.project.gradient,
      id: this.project.id,
      imgJpeg: this.project.imgJpeg,
      products: [this.project.products.find((p) => p.name === this.batch.productName)],
      name: this.project.name,
      batchComments: this.project.batchComments
    } as Iproject;
    this.router.navigate(routerLink, { state: { project: projectForVector, batch: this.batch }});
  }

  /**
   * Check for existing calculated forecast and show accordingly.
   */
  private initForecast(forecasts: any[]): void {
    let forecastItem;
    this.storedForecasts = forecasts;
    if (!this.selectedReference) {
      const forecastItemForBatch = this.findForecastsByBatch(forecasts, this.batch.batchId);
      // display the newest calculated forecast by default
      forecastItem = forecastItemForBatch[0];
    }

    if (forecasts.length && forecastItem) {
      // sets KPI value
      this.calculatedCPI = forecastItem.value;
      // sets selected reference
      this.selectedReference = this.referenceBatches.find(
        (item) => {
          return item.id === forecastItem.reference;
        });
      this.eventForecastInit.emit(forecastItem.selectedForecastMeasurement);
      if (forecastItem.retainMeasurementId) {
        this.selectedRetainMeasurement = this.retainMeasurements.find(
          (item) => {
            return item.id === forecastItem.retainMeasurementId;
          });
        if (this.selectedRetainMeasurement) {
          this.selectedReferenceTypeChanged({value: this.RETAIN_MEASUREMENT} as MatButtonToggleChange)
        }
      }
    }
  }

  /**
   * Call service and return newly calculated forecast.
   */
  private loadCalculatedForecast(): Observable<IBatch> {
    return this.projectService.getForecastBatchById({
      id: this.batch.batchId,
      reference: this.selectedReference.id,
      forecastMeasurementId: this.selectedForecastMeasurement.id,
      retainMeasurementId: this.selectedRetainMeasurement ? this.selectedRetainMeasurement.id : null
    });
  }

  private loadRetainMeasurements(): void {
    this.isLoading = true;
    this.projectService.getRetainMeasurementsByBatchId({ batchId: this.batch.batchId }).subscribe({
      next: (retainMeasurements: IBatchForecastMeasurement[]) => {
        if (retainMeasurements) {
          this.retainMeasurements = retainMeasurements;
        }
      },
      error: () => {
        // nothing
        this.retainMeasurements = [];
      },
      complete: () => {
        this.isLoading = false;
        // when retainMeasurements were loaded check for stored forecasts
        this.calculatedForecasts$.pipe(takeUntil(this.stop$)).subscribe(forecasts => {
          this.initForecast(forecasts);
        });
      },
    });
  }

  /**
   * Find calculatedForecast by unique batch ID in Array sort by timestamp.
   */
  private findForecastsByBatch(forecasts: any[], id: number): any[] {
    return forecasts.filter((item) => {
      return item.batchId === id;
    }).sort((a, b) => b.forecastCreatedTime - a.forecastCreatedTime);
  }

  /**
   * Create object which can be placed in store.
   */
  private createForecastObject(forecast: IBatch): any {
    return Object.assign(
      { batchId: forecast.batchId},
      forecast.cpiForecast,
      { reference: this.selectedReference.id, selectedForecastMeasurement: this.selectedForecastMeasurement.id, forecastCreatedTime: new Date().getTime(), retainMeasurementId: this.selectedRetainMeasurement ? this.selectedRetainMeasurement.id : null }
    );
  }

  /**
   * Displays a stored forecast for the current selection if available
   * @private
   */
  private showSelectedForecast() {
    if (this.storedForecasts) {
      const storedForecast = this.findForecast(this.storedForecasts, this.batch.batchId, this.selectedReference, this.selectedForecastMeasurement, this.selectedRetainMeasurement);
      if (storedForecast) {
        this.calculatedCPI = storedForecast.value;
      } else {
        this.calculatedCPI = undefined;
      }
    }
  }

  /**
   * Find calculatedForecast by unique batch ID, reference batch and forecast measurement in Array.
   */
  private findForecast(forecasts: any[], id: number, selectedReference: any, selectedForecastMeasurement: IBatchForecastMeasurement, selectedRetainMeasurement: IBatchForecastMeasurement): any {
    if (selectedReference && selectedForecastMeasurement) {
      return forecasts.find((item) => {
        return item.batchId === id && item.reference === selectedReference.id && item.selectedForecastMeasurement === selectedForecastMeasurement.id && item.retainMeasurementId === (selectedRetainMeasurement ? selectedRetainMeasurement.id : null);
      });
    }
    return null;
  }

  private addForecastToStore(forecast: any): void {
    this.isLoading = false;

    this.store.dispatch(new ForecastActions.AddOrUpdateForecast(forecast));

    this.calculatedCPI = forecast.value;
  }
}
